"use client";

import type { ErrorLike } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { type PlainDate, toPlainDateString } from "@sa-nu/shared-utils/date";

import { graphql } from "../../../../__generated__/gql";
import type {
  AccommodationReservationProviderInput,
  SiteAvailabilityQuery,
} from "../../../../__generated__/gql/graphql";

// AvailabilityStrip 専用クエリ。RoomTypeBrowse は日程フィルタ有効時のみ nights を取得する設計
// (use-room-type-search.ts 参照) だが、AvailabilityStrip は日程フィルタの有無に関わらず常に
// 週分の日別空き状況が要るため噛み合わない。
const SiteAvailabilityQueryDocument = graphql(`
  query SiteAvailability(
    $provider: AccommodationReservationProviderInput!
    $firstNight: PlainDate!
    $lastNight: PlainDate!
    $facilitySlugs: [String!]!
    $evChargerAvailable: Boolean
    $architectureTypes: [String!]!
    $largeOnly: Boolean
  ) {
    areasForReservation(provider: $provider) {
      id
      sitesForReservation(provider: $provider) {
        id
        availability(
          firstNight: $firstNight
          lastNight: $lastNight
          provider: $provider
          facilitySlugs: $facilitySlugs
          evChargerAvailable: $evChargerAvailable
          architectureTypes: $architectureTypes
          largeOnly: $largeOnly
        ) {
          night
          isAvailable
          fromPrice
        }
      }
    }
  }
`);

export type SiteAvailabilityArea =
  SiteAvailabilityQuery["areasForReservation"][number];
export type SiteAvailabilitySite =
  SiteAvailabilityArea["sitesForReservation"][number];
export type SiteAvailabilityNight =
  SiteAvailabilitySite["availability"][number];

/**
 * 指定 site の日別空き状況・最低価格を、表示範囲（debouncedRangeStart 起点 rangeSize 泊）分取得する。
 * debouncedRangeStart は AvailabilityStripProvider が週送り連打を吸収した後の値であり、
 * 即時値の rangeStart をそのまま渡すと連打のたびにクエリが発火してしまう。
 * クエリ自体は areasForReservation 配下の全 site を返す（RoomTypeBrowse と同型）ため、
 * 同一 variables で複数 SiteCard から呼ばれても Apollo のクエリ重複排除で 1 回のフェッチに集約される。
 */
export function useSiteAvailability({
  siteId,
  debouncedRangeStart,
  rangeSize,
  isRangeSizeResolved,
  provider,
  facilitySlugs,
  evChargerAvailable,
  architectureTypes,
  largeOnly,
}: {
  siteId: string;
  debouncedRangeStart: PlainDate;
  rangeSize: number;
  isRangeSizeResolved: boolean;
  provider: AccommodationReservationProviderInput;
  facilitySlugs: string[];
  evChargerAvailable: boolean;
  architectureTypes: string[];
  largeOnly: boolean;
}): {
  nights: SiteAvailabilityNight[];
  loading: boolean;
  error: ErrorLike | undefined;
} {
  const lastNight = debouncedRangeStart.add({ days: rangeSize - 1 });

  const { data, loading, error } = useQuery(SiteAvailabilityQueryDocument, {
    variables: {
      provider,
      firstNight: toPlainDateString(debouncedRangeStart),
      lastNight: toPlainDateString(lastNight),
      facilitySlugs,
      // false は BE に「フィルタなし」として伝えるため null に変換する
      evChargerAvailable: evChargerAvailable ? true : null,
      architectureTypes,
      largeOnly: largeOnly ? true : null,
    },
    // rangeSize が sp→pc の2段階で確定するため、確定前のクエリ発火を止めて重複フェッチを防ぐ
    skip: !isRangeSizeResolved,
    fetchPolicy: "cache-first",
  });

  const site = (data?.areasForReservation ?? [])
    .flatMap((area) => area.sitesForReservation)
    .find((s) => s.id === siteId);

  return {
    nights: site?.availability ?? [],
    loading,
    error,
  };
}
